<!-- Modern Vehicle Location Detail Modal -->
<template>
  <modal
    name="location-detail-modal"
    :height="isMobile ? '100%' : 'auto'"
    :width="isMobile ? '100%' : '85%'"
    :scrollable="true"
    @opened="getAll()"
    @closed="reset()"
    :clickToClose="false"
    transition="fade"
  >
    <div class="w-full bg-white dark:bg-gray-900 rounded shadow overflow-hidden" style="height:80vh">
      <!-- Minimal Header -->
      <div class="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-3">
            <h2 class="text-base font-semibold text-gray-900 dark:text-gray-100">{{ vehicle || 'Araç Detayı' }}</h2>
            <span v-if="detail.driverName" class="text-xs text-gray-500 dark:text-gray-400">
              {{ detail.driverName }}
            </span>
            <span v-if="!load" class="flex items-center text-xs text-gray-500">
              <div class="w-1.5 h-1.5 bg-green-500 rounded-full mr-1"></div>
              Canlı
            </span>
          </div>

          <!-- Header Actions -->
          <div class="flex items-center space-x-2">
            <!-- Last Update Info -->
            <span v-if="lastUpdateTime" class="text-xs text-gray-400 mr-2">
              {{ lastUpdateTime | dateFormat }}
            </span>

            <!-- Refresh Button -->
            <button
              @click="refreshData"
              :disabled="load"
              class="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors disabled:opacity-50"
              title="Yenile"
            >
              <i class="fas fa-sync-alt text-sm" :class="{ 'animate-spin': load }"></i>
            </button>

            <!-- Close Button -->
            <button
              @click="$modal.hide('location-detail-modal')"
              class="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              title="Kapat"
            >
              <i class="fas fa-times text-sm"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- Content Area -->
      <div class="flex h-full">
        <!-- Map Section -->
        <div class="flex-1 relative">
          <!-- Loading Overlay -->
          <div v-if="load" class="absolute inset-0 bg-white dark:bg-gray-900 z-10 flex items-center justify-center">
            <div class="text-center">
              <div class="inline-block w-8 h-8 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin mb-2"></div>
              <p class="text-xs text-gray-500 dark:text-gray-400">Yükleniyor...</p>
            </div>
          </div>

          <!-- Map Controls -->
          <div class="absolute top-3 right-3 z-20 space-y-1">
            <!-- Map Type Toggle -->
            <button
              @click="toggleMapView"
              class="w-8 h-8 bg-white dark:bg-gray-800 shadow border border-gray-200 dark:border-gray-700 rounded flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              title="Harita Türü"
            >
              <i class="fas fa-layer-group text-xs text-gray-500 dark:text-gray-400"></i>
            </button>

            <!-- Traffic Toggle -->
            <button
              @click="toggleTrafficLayer"
              :class="showTrafficLayer ? 'bg-blue-500 text-white border-blue-500' : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700'"
              class="w-8 h-8 shadow border rounded flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              title="Trafik"
            >
              <i class="fas fa-road text-xs"></i>
            </button>

            <!-- Zoom Controls -->
            <div class="bg-white dark:bg-gray-800 shadow border border-gray-200 dark:border-gray-700 rounded overflow-hidden">
              <button
                @click="zoomIn"
                class="w-8 h-8 flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                title="Yakınlaştır"
              >
                <i class="fas fa-plus text-xs text-gray-500 dark:text-gray-400"></i>
              </button>
              <div class="border-t border-gray-200 dark:border-gray-700"></div>
              <button
                @click="zoomOut"
                class="w-8 h-8 flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                title="Uzaklaştır"
              >
                <i class="fas fa-minus text-xs text-gray-500 dark:text-gray-400"></i>
              </button>
            </div>

            <!-- Center to Vehicle -->
            <button
              @click="centerToVehicle"
              class="w-8 h-8 bg-white dark:bg-gray-800 shadow border border-gray-200 dark:border-gray-700 rounded flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              title="Araca Odaklan"
            >
              <i class="fas fa-crosshairs text-xs text-gray-500 dark:text-gray-400"></i>
            </button>
          </div>

          <!-- Map Component -->
          <map-display
            :position="position"
            :zoom-level="zoomLevel"
            :map-type="$store.state.mapView ? 'hybrid' : 'roadmap'"
            :markers="markers"
            :state-id="stateId"
            :detail="detail"
            :task-detail="taskDetail"
            :show-traffic="showTrafficLayer"
            ref="mapDisplay"
            @map-clicked="handleMapClick"
            @marker-clicked="handleMarkerClick"
            class="h-full w-full"
          />

          <!-- Journey Progress Bar (Bottom Overlay) -->
          <div
            v-if="!load && markers.length && isJourneyStateActive() && taskDetail.totalDistance"
            class="absolute bottom-3 left-3 right-3 z-20"
          >
            <div class="bg-white dark:bg-gray-800 rounded shadow border border-gray-200 dark:border-gray-700 p-3">
              <div class="flex items-center justify-between mb-2">
                <span class="text-xs font-medium text-gray-700 dark:text-gray-300">{{ detail.state }}</span>
                <span class="text-xs text-gray-500">{{ calculateJourneyProgress() }}%</span>
              </div>

              <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 mb-2">
                <div
                  class="bg-blue-500 h-1.5 rounded-full transition-all duration-500"
                  :style="`width: ${calculateJourneyProgress()}%`"
                ></div>
              </div>

              <div class="grid grid-cols-3 gap-3 text-xs">
                <div>
                  <p class="text-gray-500 dark:text-gray-400">Mesafe</p>
                  <p class="font-medium text-gray-900 dark:text-gray-100">{{ taskDetail.totalDistance || 0 }} km</p>
                </div>
                <div>
                  <p class="text-gray-500 dark:text-gray-400">Tahmini Varış</p>
                  <p class="font-medium text-gray-900 dark:text-gray-100">{{ taskDetail.estimatedArrival | timeFormat }}</p>
                </div>
                <div>
                  <p class="text-gray-500 dark:text-gray-400">Hız</p>
                  <p class="font-medium text-gray-900 dark:text-gray-100">{{ detail.speed || 0 }} km/h</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Side Panel -->
        <div class="w-72 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 flex flex-col">
          <!-- Vehicle Info Card -->
          <div class="p-3 border-b border-gray-200 dark:border-gray-700">
            <h3 class="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Araç Bilgileri</h3>
            <div class="space-y-2">
              <div class="flex justify-between text-xs">
                <span class="text-gray-500 dark:text-gray-400">Plaka</span>
                <span class="font-medium text-gray-900 dark:text-gray-100">{{ detail.vehicle || '-' }}</span>
              </div>
              <div class="flex justify-between text-xs">
                <span class="text-gray-500 dark:text-gray-400">Dorse</span>
                <span class="font-medium text-gray-900 dark:text-gray-100">{{ detail.dorse || '-' }}</span>
              </div>
              <div class="flex justify-between text-xs">
                <span class="text-gray-500 dark:text-gray-400">Bölge</span>
                <span class="font-medium text-gray-900 dark:text-gray-100">{{ detail.region || '-' }}</span>
              </div>
              <div class="flex justify-between text-xs">
                <span class="text-gray-500 dark:text-gray-400">Adres</span>
                <span class="font-medium text-gray-900 dark:text-gray-100 text-right max-w-36 truncate" :title="detail.adress">{{ detail.adress || '-' }}</span>
              </div>
            </div>
          </div>

          <!-- Waypoints -->
          <div class="flex-1 p-3 overflow-y-auto" v-if="detail.waypoints && detail.waypoints.length">
            <h3 class="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
              Durak Noktaları ({{ detail.waypoints.length }})
            </h3>

            <div class="space-y-2">
              <div
                v-for="(waypoint, index) in detail.waypoints"
                :key="index"
                class="bg-gray-50 dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700 p-2"
              >
                <div class="flex items-center justify-between mb-1">
                  <span class="text-xs font-medium text-gray-700 dark:text-gray-300">{{ waypoint.no || `Durak ${index + 1}` }}</span>
                  <span class="text-xs text-gray-500">{{ waypoint.amount || 0 }} ton</span>
                </div>
                <p class="text-xs text-gray-600 dark:text-gray-400 leading-tight">{{ waypoint.position.adress || '-' }}</p>
                <p v-if="waypoint.date" class="text-xs text-gray-400 mt-1">{{ waypoint.date | dateFormat }}</p>
              </div>
            </div>
          </div>

          <!-- No Data State -->
          <div v-if="!load && markers.length === 0" class="flex-1 flex items-center justify-center p-4">
            <div class="text-center">
              <i class="fas fa-exclamation-triangle text-3xl text-gray-300 mb-3"></i>
              <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">Konum Bulunamadı</h3>
              <p class="text-xs text-gray-500 mb-3">Bu araç için güncel konum bilgisi alınamadı.</p>
              <button
                @click="refreshData"
                class="px-3 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
              >
                <i class="fas fa-redo mr-1"></i>Tekrar Dene
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </modal>
</template>

<script>
// Alt Bileşenler
import MapDisplay from "./components/MapDisplay.vue";

// NPM
import moment from "moment";

// Networking
import axios from "axios";
import { tracking } from "@/networking/urlmanager";
import { routeEngine } from "@/networking/routeEngine";

//Utils
import { decode } from "../../../../../../utils/encoder";

export default {
  name: "vehicle-location-detail",
  components: {
    MapDisplay,
  },
  props: ["vehicle", "stateId"],
  data() {
    return {
      position: { lat: 41.015137, lng: 28.97953 },
      center: { lat: 41.015137, lng: 28.97953 },
      zoomLevel: 5,
      markers: [],
      detail: {},
      taskDetail: {},
      load: true,
      showTrafficLayer: false,
      refreshInterval: null,
      lastUpdateTime: null,
    };
  },
  computed: {
    isMobile() {
      return window.innerWidth < 768;
    },
    timeElapsedSinceUpdate() {
      if (!this.lastUpdateTime) return null;
      const now = moment();
      const duration = moment.duration(now.diff(this.lastUpdateTime));
      return Math.floor(duration.asMinutes());
    },
  },
  mounted() {
    // Otomatik yenileme için interval başlat (2 dakikada bir)
    this.refreshInterval = setInterval(() => {
      if (!this.load) {
        this.refreshData(false); // Sessiz yenileme
      }
    }, 2 * 60 * 1000);

    // Harita boyutunu pencere boyutuna göre ayarla
    window.addEventListener("resize", this.handleResize);
  },
  beforeDestroy() {
    // Interval'i temizle
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }

    window.removeEventListener("resize", this.handleResize);
  },
  methods: {
    handleResize() {
      // Pencere boyutu değiştiğinde harita bileşenini güncelle
      if (this.$refs.mapDisplay) {
        this.$refs.mapDisplay.resizeMap();
      }
    },
    isJourneyStateActive() {
      return (
        this.stateId == 1 || this.stateId == 2 || this.stateId == 3 || this.stateId == 5
      );
    },
    calculateJourneyProgress() {
      // Basit bir progress hesaplaması - gerçek implementasyon iş mantığına göre ayarlanabilir
      if (!this.taskDetail.totalDistance || !this.detail.speed) return 0;
      
      // Örnek hesaplama - state'e göre progress
      const stateProgress = {
        1: 10,  // Yükleme
        2: 30,  // Yolda
        3: 60,  // Teslim noktasında
        4: 90,  // Teslim edildi
        5: 95   // Dönüş
      };
      
      return stateProgress[this.stateId] || 0;
    },
    toggleMapView() {
      this.$store.commit('SET_MAP_VIEW', !this.$store.state.mapView);
    },
    refreshData(showLoadingIndicator = true) {
      // Verileri yeniden yükle
      this.getAll(showLoadingIndicator);
    },
    getAll(showLoadingIndicator = true) {
      if (showLoadingIndicator) {
        this.markers = [];
        this.detail = {};
        this.taskDetail = {};
        this.load = true;
      }

      // Not: targetVehicle parametresi backend'de düzgün çalışmıyor, tüm araçları çekip frontend'de filtreliyoruz
      axios
        .get(tracking.getAll + "?isActive=true", {
          headers: {
            Authorization: "Bareer " + this.$store.state.userData.token,
          },
        })
        .then((res) => {
          this.load = false;
          this.lastUpdateTime = moment();

          if (Array.isArray(res.data.data)) {
            if (res.data.data.length > 0) {
              let markers = res.data.data.filter((r) => {
                const apiVehicle = r.vehicle.toLowerCase().replace(/\s/g, "").trim();
                const searchVehicle = this.vehicle.toLowerCase().replace(/\s/g, "").trim();
                return apiVehicle === searchVehicle;
              });

              if (markers.length > 0) {
                this.center = {
                  lat: markers[0].latitude,
                  lng: markers[0].longitude,
                };

                this.position = {
                  lat: markers[0].latitude,
                  lng: markers[0].longitude,
                };

                this.markers = this.processMarkers(markers);
                this.detail = this.markers[0];
                this.zoomLevel = 12;
                this.getRoute();

                // Bildirim göster (sadece sessiz yenileme ise)
                if (!showLoadingIndicator) {
                  this.$toast.success("Konum bilgileri güncellendi", {
                    position: "bottom-right",
                    duration: 2000,
                  });
                }
              }
            }
          }
        })
        .catch((err) => {
          console.error("API Error:", err);
          this.load = false;

          this.$toast.error(
            "Veriler yüklenirken bir hata oluştu. Lütfen tekrar deneyin.",
            {
              position: "bottom-right",
              duration: 4000,
            }
          );
        });
    },

    processMarkers(markers) {
      return markers.map((el) => {
        let list = [];
        try {
          list = JSON.parse(el.planning?.waypoints || '[]');
          if (!Array.isArray(list)) list = [];
        } catch (e) {
          list = [];
        }

        return {
          id: el.id,
          title: el.vehicle,
          region: el.region,
          position: {
            lat: parseFloat(el.latitude),
            lng: parseFloat(el.longitude),
          },
          adress: el.address,
          vehicle: el.vehicle,
          dorse: el.dorse,
          planning: el.planning,
          driverName: el.driverName,
          state: el.state,
          speed: el.speed || 0,
          speedDirection: el.speedDirection,
          lastUpdateTime: el.lastUpdateTime,
          targetDate: moment(el.targetDate).format("LLLL"),
          waypoints: list
            .filter((item) => {
              // Null/undefined veya boş waypoints'leri filtrele
              return item && (item.adress || item.latitude || item.longitude);
            })
            .map((item, index) => {
              return {
                date: item.date,
                no: item.no || `Durak ${index + 1}`,
                amount: item.amount || 0,
                position: {
                  adress: item.adress || '-',
                  lat: Number(item.latitude) || 0,
                  lng: Number(item.longitude) || 0,
                },
                icon: require("@/assets/marker/waypoint.png"),
              };
            }),
          target:
            el.planning && el.planning.state < 4
              ? {
                  adress: el.outpointAdress,
                  lat: Number(el.outpointLat),
                  lng: Number(el.outpointLng),
                }
              : {
                  adress: el.targetAdress,
                  lat: Number(el.targettLat),
                  lng: Number(el.targettLng),
                },
          isBulkOperation: el.isBulkOperation,
          icon: this.positionMarker(el.speed, el.speedDirection),
        };
      });
    },

    positionMarker(speed, direction) {
      // Hız ve yöne göre ikon belirleme
      if (speed > 5) {
        return require("@/assets/marker/yellow-truck.png");
      } else {
        return require("@/assets/marker/red-truck.png");
      }
    },

    toggleTrafficLayer() {
      this.showTrafficLayer = !this.showTrafficLayer;
    },

    zoomIn() {
      if (this.zoomLevel < 18) {
        this.zoomLevel += 1;
      }
    },

    zoomOut() {
      if (this.zoomLevel > 3) {
        this.zoomLevel -= 1;
      }
    },

    centerToVehicle() {
      if (this.markers.length > 0) {
        this.position = this.markers[0].position;
        this.zoomLevel = 12;
      }
    },

    handleMapClick(event) {
      // Haritada boş bir alana tıklandığında - özel işlem yok
    },

    handleMarkerClick(marker) {
      // Araç işaretçisine tıklandığında - özel işlem yok
    },

    getRoute() {
      if (
        !(
          this.detail.planning &&
          (this.detail.planning.state == 1 ||
            this.detail.planning.state == 2 ||
            this.detail.planning.state == 3 ||
            this.detail.planning.state == 5)
        )
      ) {
        return;
      }

      const requestData = {
        from: this.detail.position,
        to: this.detail.target,
        waypoints: this.detail.waypoints.map((item) => {
          return {
            adress: item.position.adress,
            lat: item.position.lat,
            lng: item.position.lng,
          };
        }),
        isToll: 1,
        routeType: "fast",
        shippedHazardousGoods: 1,
        isShip: 1,
        isAsphalt: 1,
        fuelRatio: 35,
        isPolyline: 1,
        vehicleTypeId: 5,
        isApi: 1,
      };

      axios
        .post(routeEngine.generate, requestData, {
          headers: {
            Authorization: "Bareer " + this.$store.state.routeEngineToken,
          },
        })
        .then((res) => {
          const detail = res.data.data;

          this.taskDetail = {
            state: this.detail.state,
            polyline: decode(detail.routePolyline).polyline,
            customer: this.detail.customer,
            totalDistance: detail.totalDistance,
            fuelConsumption: detail.fuelConsumption,
            totalMinute: detail.totalMinute,
            estimatedArrival: moment().add(detail.totalMinute, "minutes"),
          };
        })
        .catch((err) => {
          console.error("Route error:", err);
          this.$toast.error("Rota bilgileri yüklenirken bir hata oluştu.");
        });
    },

    reset() {
      this.load = true;
      this.position = { lat: 41.015137, lng: 28.97953 };
      this.zoomLevel = 5;
      this.markers = [];
      this.detail = {};
      this.taskDetail = {};
      this.showTrafficLayer = false;
    },
  },
  filters: {
    dateFormat(val) {
      return moment(val).format("DD.MM.YYYY HH:mm");
    },
    timeFormat(val) {
      return moment(val).format("HH:mm");
    },
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* Minimal scrollbar */
::-webkit-scrollbar {
  width: 3px;
}

::-webkit-scrollbar-track {
  background: transparent;
}

::-webkit-scrollbar-thumb {
  background: rgba(156, 163, 175, 0.3);
  border-radius: 2px;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(156, 163, 175, 0.5);
}
</style>